import { useCallback, useEffect, useState } from 'react';

import { gmailInviteService, InvitePeoplePage, InvitePerson } from '../services/gmailInviteService';

export const PAGE_SIZE = 50;

/** One page of the people to choose from, searched by name or address. */
const usePeoplePage = (batchId: string, general: boolean) => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<InvitePeoplePage | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      void gmailInviteService.people(batchId, { q: query, page, pageSize: PAGE_SIZE, general })
        .then((next) => { if (!cancelled) setResult(next); })
        .catch(() => undefined);
    };
    // Typing waits a moment so each keystroke is not a request; paging loads at once.
    const handle = query ? window.setTimeout(load, 250) : null;

    if (!query) load();

    return () => { cancelled = true; if (handle !== null) window.clearTimeout(handle); };
  }, [batchId, query, page, general]);

  const search = useCallback((next: string) => { setQuery(next); setPage(1); }, []);

  return { page, query, result, search, setPage };
};

/** Tick or untick one person; at the day's limit, new ticks wait until someone is unticked. */
const flip = (current: Set<string>, email: string, limit: number): Set<string> => {
  const next = new Set(current);

  if (next.has(email)) next.delete(email);
  else if (next.size < limit) next.add(email);

  return next;
};

/** Start with the first ``initial`` people ticked, once the list of first people is known (again after it changes). */
const useSeed = (first: string[], seedSize: number, apply: (emails: string[]) => void) => {
  const [seed, setSeed] = useState('');
  const seedKey = first.join('\n');

  if (first.length > 0 && seed !== seedKey) {
    setSeed(seedKey);
    apply(first.slice(0, seedSize));
  }
};

const useSelection = (first: string[], limit: number, initial: number) => {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [quick, setQuick] = useState<number | null>(initial);
  const toggle = useCallback((email: string) => {
    setQuick(null);
    setSelected((current) => flip(current, email, limit));
  }, [limit]);
  const pick = useCallback((count: number) => {
    setQuick(count);
    setSelected(new Set(first.slice(0, Math.min(count, limit))));
  }, [first, limit]);

  useSeed(first, Math.min(initial, limit), (emails) => setSelected(new Set(emails)));

  return { pick, quick, selected, toggle };
};

export const isPickable = (person: InvitePerson, general: boolean): boolean => (
  !general || (!person.member && !person.invited)
);

export interface ContactPickerState {
  first: string[];
  page: number;
  pages: number;
  people: InvitePerson[];
  loading: boolean;
  query: string;
  search: (query: string) => void;
  selected: Set<string>;
  quick: number | null;
  /** Tick the first N (0 clears). */
  pick: (count: number) => void;
  setPage: (page: number) => void;
  toggle: (email: string) => void;
  total: number;
}

/** The searchable, paged contact list and who is ticked in it (at most ``limit``, the invites left today). */
export const useContactPicker = (
  batchId: string, general: boolean, limit: number, initial = 20,
): ContactPickerState => {
  const { page, query, result, search, setPage } = usePeoplePage(batchId, general);
  const [first, setFirst] = useState<string[]>([]);

  if (result && !query && result.first.join('\n') !== first.join('\n')) setFirst(result.first);
  const selection = useSelection(first, limit, initial);
  const total = result?.total ?? 0;

  return {
    ...selection,
    first,
    loading: result === null,
    page,
    pages: Math.max(1, Math.ceil(total / PAGE_SIZE)),
    people: result?.people ?? [],
    query,
    search,
    setPage,
    total,
  };
};
